import React, {ReactNode} from 'react';

type OnlyFunctionKeys<TProps> = {
  [k in keyof TProps]?: Function;
};

// A bound function will have the first argument "consumed". If the types don't match, or the function
// has more than 5+ arguments, we'll safely return `unknown`.
type GetSingleBind<TBoundArg, TFn> = TFn extends (arg0: TBoundArg) => infer R
  ? () => R
  : TFn extends (arg0: TBoundArg, arg1: infer A1) => infer R
    ? (arg1: A1) => R
    : TFn extends (arg0: TBoundArg, arg1: infer A1, arg2: infer A2) => infer R
      ? (arg1: A1, arg2: A2) => R
      : TFn extends (arg0: TBoundArg, arg1: infer A1, arg2: infer A2, arg3: infer A3) => infer R
        ? (arg1: A1, arg2: A2, arg3: A3) => R
        : unknown;

type BindAll<TBoundArg, TProps> = {
  [k in keyof TProps]: GetSingleBind<TBoundArg, TProps[k]>;
};

// IntermediateBinder's props can't be modeled perfectly in TypeScript. While `boundArg` and `children` have
// specific types, all other prop must be a Function. Effectively, we want:
// type IntermediateBinderProps<TProps, TBoundArg> = {
//   boundArg: TBoundArg;
//   children: (...) => ReactNode;
//   [string not boundArg | children]: Function,
// } & TProps
//
// However, TypeScript doesn't have any way of modeling "any string except for ..." with index signatures.
// Instead, we require that all values in TProps are Functions via `TProps extends OnlyFunctionKeys<TProps>`,
// and rely on the fact that TypeScript will yell at the user for all explicit usage of <IntermediateBinder />
// with a prop that is not specified in TProps.
export type IntermediateBinderProps<TProps extends OnlyFunctionKeys<TProps>, TBoundArg> = TProps & {
  boundArg: TBoundArg;
  children: (boundFns: BindAll<TBoundArg, TProps>) => ReactNode;
};

/**
 * This component prevents re-renders by pre-binding functions.
 * For context, see:
 *    https://benchling.engineering/performance-engineering-with-react-e03013e53285
 */

class IntermediateBinder<TProps extends OnlyFunctionKeys<TProps>, TBoundArg> extends React.Component<
  IntermediateBinderProps<TProps, TBoundArg>
> {
  _boundFns: {
    [k: string]: Function;
  } = {};

  override UNSAFE_componentWillMount = (): void => {
    this.rebindFns(this.props as IntermediateBinderProps<TProps, TBoundArg>, true);
  };

  override UNSAFE_componentWillReceiveProps = (
    nextProps: IntermediateBinderProps<TProps, TBoundArg>
  ): void => {
    this.rebindFns(nextProps, this.props.boundArg !== nextProps.boundArg);
  };

  private rebindFns = (props: IntermediateBinderProps<TProps, TBoundArg>, bindAll: boolean): void => {
    const newFns: {[k: string]: Function} = {};
    for (const name of Object.keys(props) as Array<
      string & keyof IntermediateBinderProps<TProps, TBoundArg>
    >) {
      const value = props[name];
      if (!['boundArg', 'children'].includes(name)) {
        if (bindAll || value !== (this.props[name] as unknown)) {
          newFns[name] = (value as Function).bind(null, props.boundArg);
        } else {
          newFns[name] = this._boundFns[name];
        }
      }
    }
    this._boundFns = newFns;
  };

  override render = (): ReactNode => (this.props.children as Function)(this._boundFns);
}

export default IntermediateBinder;
